import axios from "axios";
import { useContext, useEffect, useState, useCallback } from "react";
import { Table, DatePicker, Input, Button, message, Modal, Badge } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";

const { TextArea } = Input;

const STATUS_TABS = [
    {
        key: "PENDING",
        label: "Pending",
        icon: <ClockCircleOutlined />,
        activeColor: "#faad14",
    },
    {
        key: "APPROVED",
        label: "Approved",
        icon: <CheckCircleOutlined />,
        activeColor: "#52c41a",
    },
    {
        key: "REJECTED",
        label: "Rejected",
        icon: <CloseCircleOutlined />,
        activeColor: "#ff4d4f",
    },
];

export default function ViewImproperHr() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [activeTab, setActiveTab] = useState("PENDING");

    const [pageSize, setPageSize] = useState(50);

    // Tab counts
    const [tabCounts, setTabCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });

    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        employeeName: "",
        employeeCode: "",
    });

    // Row selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Modal state (single)
    const [modalOpen, setModalOpen] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Bulk modal state
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkAction, setBulkAction] = useState(null);
    const [bulkRemarks, setBulkRemarks] = useState("");
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const buildQueryParams = useCallback(
        (tab = activeTab, overrideFilters = null) => {
            const params = new URLSearchParams();
            const f = overrideFilters ?? filters;

            params.set("status", tab);

            if (f.fromDate) params.set("from_date", f.fromDate.format("YYYY-MM-DD"));
            if (f.toDate) params.set("to_date", f.toDate.format("YYYY-MM-DD"));
            if (f.employeeName.trim()) params.set("emp_name", f.employeeName.trim());
            if (f.employeeCode.trim()) params.set("emp_code", f.employeeCode.trim());

            return params.toString();
        },
        [activeTab, filters]
    );

    // Fetch counts for all tabs (with current filters but ignoring status)
    const fetchAllCounts = useCallback(
        async (overrideFilters = null) => {
            try {
                const f = overrideFilters ?? filters;
                const baseParams = new URLSearchParams();
                if (f.fromDate) baseParams.set("from_date", f.fromDate.format("YYYY-MM-DD"));
                if (f.toDate) baseParams.set("to_date", f.toDate.format("YYYY-MM-DD"));
                if (f.employeeName?.trim()) baseParams.set("emp_name", f.employeeName.trim());
                if (f.employeeCode?.trim()) baseParams.set("emp_code", f.employeeCode.trim());

                const statuses = ["PENDING", "APPROVED", "REJECTED"];
                const results = await Promise.all(
                    statuses.map((status) => {
                        const params = new URLSearchParams(baseParams);
                        params.set("status", status);
                        return axios
                            .get(`${backUrl}/api/hr/regularization/all-requests/?${params.toString()}`, {
                                headers: { Authorization: `Bearer ${authData?.access}` },
                            })
                            .then((res) => {
                                const data = res.data.results ?? res.data;
                                return res.data.count ?? (Array.isArray(data) ? data.length : 0);
                            })
                            .catch(() => 0);
                    })
                );

                setTabCounts({
                    PENDING: results[0],
                    APPROVED: results[1],
                    REJECTED: results[2],
                });
            } catch (error) {
                console.error("Error fetching counts:", error);
            }
        },
        [backUrl, authData, filters]
    );

    const fetchData = useCallback(
        async (tab = activeTab, overrideFilters = null) => {
            try {
                const queryString = buildQueryParams(tab, overrideFilters);
                const res = await axios.get(
                    `${backUrl}/api/hr/regularization/all-requests/?${queryString}`,
                    { headers: { Authorization: `Bearer ${authData?.access}` } }
                );
                setRequests(res.data.results || res.data);
            } catch (error) {
                console.error("Error fetching:", error.response || error.message);
            }
        },
        [backUrl, authData, buildQueryParams, activeTab]
    );

    // Fetch on tab change
    useEffect(() => {
        setSelectedRowKeys([]);
        fetchData(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Fetch counts on mount
    useEffect(() => {
        fetchAllCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        fetchData(activeTab);
        fetchAllCounts();
    };

    const handleReset = () => {
        const clearedFilters = { fromDate: null, toDate: null, employeeName: "", employeeCode: "" };
        setFilters(clearedFilters);
        fetchData(activeTab, clearedFilters);
        fetchAllCounts(clearedFilters);
    };

    // ── Single record modal ──────────────────────────────────────────────────
    const openModal = (record, action) => {
        setSelectedRecord(record);
        setModalAction(action);
        setRemarks("");
        setModalOpen(true);
    };

    const handleModalSubmit = async () => {
        if (!remarks.trim()) {
            message.warning("Please enter remarks before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/regularization/bulk-process/`,
                {
                    request_ids: [selectedRecord.id],
                    status: modalAction,
                    remarks: remarks.trim(),
                },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(
                `Request ${modalAction === "APPROVED" ? "approved" : "rejected"} successfully.`
            );
            setModalOpen(false);
            setSelectedRowKeys([]);
            fetchData();
            fetchAllCounts();
        } catch (error) {
            console.error("Error processing request:", error.response || error.message);
            message.error("Failed to process the request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleModalCancel = () => {
        setModalOpen(false);
        setRemarks("");
        setSelectedRecord(null);
        setModalAction(null);
    };

    // ── Bulk action modal ────────────────────────────────────────────────────
    const openBulkModal = (action) => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one request.");
            return;
        }
        setBulkAction(action);
        setBulkRemarks("");
        setBulkModalOpen(true);
    };

    const handleBulkSubmit = async () => {
        if (!bulkRemarks.trim()) {
            message.warning("Please enter remarks before submitting.");
            return;
        }
        setBulkSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/regularization/bulk-process/`,
                {
                    request_ids: selectedRowKeys,
                    status: bulkAction,
                    remarks: bulkRemarks.trim(),
                },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success("Bulk processing completed.");
            setBulkModalOpen(false);
            setSelectedRowKeys([]);
            fetchData();
            fetchAllCounts();
        } catch (error) {
            console.error("Bulk process error:", error.response || error.message);
            message.error("Bulk processing failed. Please try again.");
        } finally {
            setBulkSubmitting(false);
        }
    };

    const handleBulkModalCancel = () => {
        setBulkModalOpen(false);
        setBulkRemarks("");
        setBulkAction(null);
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const statusColors = { PENDING: "#faad14", APPROVED: "green", REJECTED: "red" };
    const getStatusColor = (status) =>
        status ? statusColors[status.toUpperCase()] || "#faad14" : "#faad14";

    const tableData = requests.map((item) => ({ key: item.id, ...item }));

    // ── Row selection (only on PENDING tab) ──────────────────────────────────
    const rowSelection =
        activeTab === "PENDING"
            ? {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                getCheckboxProps: (record) => ({
                    disabled: record.status?.toUpperCase() !== "PENDING",
                }),
            }
            : undefined;

    // ── Columns ──────────────────────────────────────────────────────────────
    const columns = [
        { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
        { title: "Employee Code", dataIndex: "employee_code", key: "employee_code" },
        {
            title: "Attendance Date",
            dataIndex: "attendance_date",
            key: "attendance_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Actual Type",
            key: "type",
            render: (_, record) =>
                record.first_in_type || record.last_out_type
                    ? <>{record.first_in_type ? "First In" : "Last Out"}</>
                    : "-",
        },
        {
            title: "Actual Mode",
            key: "mode",
            render: (_, record) =>
                record.first_in_type || record.last_out_type
                    ? <>{record.first_in_type ? record.actual_first_in_type : record.actual_last_out_type}</>
                    : "-",
        },
        {
            title: "Actual Time",
            key: "time",
            render: (_, record) => {
                const value = record.first_in_type
                    ? record.actual_first_in_value
                    : record.actual_last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Updated Type",
            key: "first_in_type",
            render: (_, record) =>
                record.first_in_type || record.last_out_type
                    ? <>{record.first_in_type ? "First In" : "Last Out"}</>
                    : "-",
        },
        {
            title: "Updated Mode",
            key: "updated_mode",
            render: (_, record) =>
                record.first_in_type || record.last_out_type
                    ? <>{record.first_in_type || record.last_out_type}</>
                    : "-",
        },
        {
            title: "Updated Time",
            key: "updated_time",
            render: (_, record) => {
                const value = record.first_in_value || record.last_out_value;
                return value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "-";
            },
        },
        {
            title: "Reason",
            dataIndex: "reason_by_employee",
            key: "reason_by_employee",
            width: 250,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "250px" }}>
                    {text || "-"}
                </div>
            ),
        },
        ...(activeTab !== "PENDING"
            ? [
                {
                    title: "Approver Remarks",
                    dataIndex: "remarks_by_approver",
                    key: "remarks_by_approver",
                    width: 200,
                    render: (text) => (
                        <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "200px" }}>
                            {text || "-"}
                        </div>
                    ),
                },
                {
                    title: "Approved On",
                    dataIndex: "approved_on",
                    key: "approved_on",
                    render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
                },
            ]
            : []
        ),
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "-"}
                </span>
            ),
        },
        ...(activeTab === "PENDING"
            ? [
                {
                    title: "Action",
                    key: "action",
                    render: (_, record) => {
                        const status = record.status?.toUpperCase();
                        if (status === "APPROVED" || status === "REJECTED")
                            return <span style={{ color: "#aaa", fontSize: 12 }}>—</span>;
                        return (
                            <div style={{ display: "flex", gap: "8px" }}>
                                <Button
                                    type="primary"
                                    shape="round"
                                    icon={<CheckCircleOutlined />}
                                    style={{
                                        backgroundColor: "#52c41a",
                                        borderColor: "#52c41a",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    onClick={() => openModal(record, "APPROVED")}
                                />
                                <Button
                                    type="primary"
                                    shape="round"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    style={{ display: "flex", alignItems: "center" }}
                                    onClick={() => openModal(record, "REJECTED")}
                                />
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];

    const isApprove = modalAction === "APPROVED";
    const isBulkApprove = bulkAction === "APPROVED";

    return (
        <>
            <center>
                <h4>Improper Attendance Requests</h4>
            </center>

            {/* Status Tab Buttons */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                }}
            >
                {STATUS_TABS.map((tab) => {
                    const isActive = activeTab === tab.key;
                    const count = tabCounts[tab.key] ?? 0;
                    return (
                        <Badge
                            key={tab.key}
                            count={count}
                            overflowCount={999}
                            style={{
                                backgroundColor: isActive ? tab.activeColor : "#d9d9d9",
                                color: "#fff",
                                fontWeight: 700,
                                boxShadow: "none",
                            }}
                        >
                            <Button
                                type={isActive ? "primary" : "default"}
                                icon={tab.icon}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    backgroundColor: isActive ? tab.activeColor : undefined,
                                    borderColor: isActive ? tab.activeColor : undefined,
                                    minWidth: 110,
                                    fontWeight: isActive ? 600 : 400,
                                }}
                            >
                                {tab.label}
                            </Button>
                        </Badge>
                    );
                })}
            </div>

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: "16px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                }}
            >
                <Input
                    placeholder="Employee Name"
                    value={filters.employeeName}
                    allowClear
                    style={{ width: isMobile ? "100%" : 180 }}
                    onChange={(e) =>
                        setFilters((prev) => ({ ...prev, employeeName: e.target.value }))
                    }
                    onPressEnter={handleSearch}
                />

                <Input
                    placeholder="Employee Code"
                    value={filters.employeeCode}
                    allowClear
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(e) =>
                        setFilters((prev) => ({ ...prev, employeeCode: e.target.value }))
                    }
                    onPressEnter={handleSearch}
                />

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    value={filters.fromDate}
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters((prev) => ({ ...prev, fromDate: date }))}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    value={filters.toDate}
                    style={{ width: isMobile ? "100%" : 160 }}
                    onChange={(date) => setFilters((prev) => ({ ...prev, toDate: date }))}
                />

                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                >
                    Search
                </Button>

                <Button onClick={handleReset}>clear</Button>
            </div>

            {/* Bulk Action Bar — only visible on PENDING tab */}
            {activeTab === "PENDING" && (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                        padding: "10px 16px",
                        backgroundColor: "#ffffff",
                        border: "none",
                        borderRadius: "6px",
                        flexWrap: "wrap",
                    }}
                >
                    <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            style={{
                                backgroundColor: selectedRowKeys.length > 0 ? "#52c41a" : undefined,
                                borderColor: selectedRowKeys.length > 0 ? "#52c41a" : undefined,
                            }}
                            onClick={() => openBulkModal("APPROVED")}
                        >
                            Bulk Approve
                        </Button>
                        <Button
                            type="primary"
                            danger
                            icon={<CloseCircleOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal("REJECTED")}
                        >
                            Bulk Reject
                        </Button>
                    </div>
                </div>
            )}

            {/* Table */}
            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={tableData}
                pagination={{
                    pageSize: pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (newPage, newPageSize) => {
                        setPageSize(newPageSize);
                    },
                }}
                scroll={{ x: true }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />

            {/* ── Single Approve / Reject Modal ─────────────────────────────── */}
            <Modal
                open={modalOpen}
                title={
                    <span style={{ color: isApprove ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
                        {isApprove ? (
                            <><CheckCircleOutlined style={{ marginRight: 8 }} />Approve Request</>
                        ) : (
                            <><CloseCircleOutlined style={{ marginRight: 8 }} />Reject Request</>
                        )}
                    </span>
                }
                onCancel={handleModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleModalCancel} disabled={submitting}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger={!isApprove}
                        style={isApprove ? { backgroundColor: "#52c41a", borderColor: "#52c41a" } : {}}
                        loading={submitting}
                        onClick={handleModalSubmit}
                    >
                        {isApprove ? "Approve" : "Reject"}
                    </Button>,
                ]}
                destroyOnClose
            >
                {selectedRecord && (
                    <div style={{ marginBottom: 16 }}>
                        <p style={{ margin: "4px 0" }}>
                            <strong>Employee:</strong> {selectedRecord.employee_name}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                            <strong>Employee Code:</strong> {selectedRecord.employee_code || "-"}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                            <strong>Date:</strong>{" "}
                            {selectedRecord.attendance_date
                                ? dayjs(selectedRecord.attendance_date).format("DD/MM/YYYY")
                                : "-"}
                        </p>
                        <p style={{ margin: "4px 0 12px" }}>
                            <strong>Employee Reason:</strong>{" "}
                            {selectedRecord.reason_by_employee || "-"}
                        </p>
                    </div>
                )}
                <TextArea
                    rows={4}
                    placeholder={`Enter remarks for ${isApprove ? "approval" : "rejection"}...`}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                />
            </Modal>

            {/* ── Bulk Approve / Reject Modal ───────────────────────────────── */}
            <Modal
                open={bulkModalOpen}
                title={
                    <span style={{ color: isBulkApprove ? "#52c41a" : "#ff4d4f", fontWeight: 600 }}>
                        {isBulkApprove ? (
                            <><CheckCircleOutlined style={{ marginRight: 8 }} />Bulk Approve Requests</>
                        ) : (
                            <><CloseCircleOutlined style={{ marginRight: 8 }} />Bulk Reject Requests</>
                        )}
                    </span>
                }
                onCancel={handleBulkModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleBulkModalCancel} disabled={bulkSubmitting}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger={!isBulkApprove}
                        style={isBulkApprove ? { backgroundColor: "#52c41a", borderColor: "#52c41a" } : {}}
                        loading={bulkSubmitting}
                        onClick={handleBulkSubmit}
                    >
                        {isBulkApprove ? "Approve All" : "Reject All"}
                    </Button>,
                ]}
                destroyOnClose
            >
                <p style={{ marginBottom: 12, color: "#595959" }}>
                    Applying to <strong>{selectedRowKeys.length}</strong> selected request(s).
                </p>
                <TextArea
                    rows={4}
                    placeholder={`Enter remarks for bulk ${isBulkApprove ? "approval" : "rejection"}...`}
                    value={bulkRemarks}
                    onChange={(e) => setBulkRemarks(e.target.value)}
                    maxLength={500}
                    showCount
                />
            </Modal>
        </>
    );
}